import React, { useEffect, useRef } from 'react';
import {
  AccordionIcon,
  Box,
  Text,
  Flex,
  Divider,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Button,
} from '@chakra-ui/core';
import { AiOutlineArrowLeft } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';
import { FavoriteCandidateView } from './FavoriteCandidateView';
import { CandidateEngagementView } from './CandidateEngagementView';
import { BackgroundCheck } from './BackgroundCheck';
import { searchAndSortWrapper } from './SearchAndSortStyles';
import { useSeekersFilters } from '../filters/SeekersFiltersContext';
import { RatingsView } from './RatingsView';
import { useStoreState } from '../../../../models/hooks';
// import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import { HireStatus } from './HireStatus';
import { LanguageEvaluation } from './LanguageEvaluation';
import { AvailabilityFit } from './AvailabilityFit';
import { PriorityCandidatesView } from './PriorityCandidatesView';
import { RemoteConfigKey } from '../../../../firebase/remoteConfig';
import { KioskCandidatesView } from './KioskCandidatesView';
import { ActionRequiredCandidateView } from './ActionRequiredCandidateView';
import { RequisitionsList } from './RequisitionsList';
import { isPositionCandidatesPage } from '../../../../utils/candidatesUtils';
import { WorkBrightStatus } from './WorkBrightStatus';
import { useUserPermission } from '../../../../services/permission/useUserPermission';
import { EmployeePermissionsEnum } from '../../../administration/team/teamPermission/teamPermission.constant';
import { EngagementTypes, SearchAndSortTypes } from '../filters/seekersFiltersContextValueType';
import colors from '../../../../styles/colors';

export type SearchAndSortViewProps = {
  onCloseSearch: () => void;
  isCertnFeatureEnabled: boolean;
  isCertnEnabled: boolean;
  isWorkBrightEnabled?: boolean;
};

export const SearchAndSortView = ({
  onCloseSearch,
  isCertnFeatureEnabled,
  isCertnEnabled,
}: SearchAndSortViewProps): JSX.Element => {
  const { t } = useTranslation();
  const { checkPermission } = useUserPermission();
  const { isFromSearchAndSort, setIsFromSearchAndSort, searchAndSortTypes, setSearchAndSortTypes } = useSeekersFilters();

  const hasMounted = useRef<boolean>(false);
  const prevHasActiveFilters = useRef<boolean>(false);

  const [tempSearchAndSort, setTempSearchAndSort] = React.useState<SearchAndSortTypes>(searchAndSortTypes);
  const [filterCount, setFilterCount] = React.useState<number>(0);

  const isHireFormEnabled = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabled);
  const isLanguageEvaluationEnabled = useStoreState((s) => s.app.accounts?.configuration?.languageEvaluationEnabled);
  const hireFormEnabledPositions = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabledPositions);
  const isRequsitionEnabled = useStoreState((s) => s.app.accounts?.configuration?.requisitionEnabled);
  const isScheduleMatchEnabled = useStoreState((s) => s.app.accounts?.configuration?.scheduleMatchEnabled);

  const isPriorityFeatureEnabled: boolean = useStoreState((state) => {
    return state.remoteConfig[RemoteConfigKey.ENABLE_PRIORITY_CANDIDATE_FILTER];
  });

  // istanbul ignore next
  useEffect(() => {
    if (searchAndSortTypes.isCleared) {
      const clearedTempFilters = { ...searchAndSortTypes };
      delete clearedTempFilters.isCleared;

      setTempSearchAndSort(clearedTempFilters);

      // Remove the flag from global state to avoid re-triggering
      setSearchAndSortTypes(clearedTempFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchAndSortTypes]);

  // istanbul ignore next
  const clearAllFilters = () => {
    const clearedFilters = {
      ...searchAndSortTypes,
      isFavouriteApplied: false,
      isPriorityCandidates: false,
      kioskEnabled: false,
      isInterventionEnabled: false,
      backgroundCheck: [],
      workbrightStatus: [],
      hireStatus: [],
      languageEvaluation: [],
      availabilityFit: [],
      modernHireStatus: [],
      candidateEngagement: {
        apointmentRequested: '',
        Hired: '',
        offerMade: '',
        offerRejected: '',
        appoinmentAccepted: '',
        appoinmentDeclined: '',
        appoinmentCancled: '',
        noAppointmentRequest: '',
        offerAccepted: '',
        interestRevoked: '',
        contactRequestDeclined: '',
        onDemandInterviewRequested: '',
        onDemandInterviewAccepted: '',
        onDemandInterviewCanceled: '',
      },
      rating: [0, 0, 0, 0, 0],
      availability: {
        day: false,
        night: false,
        evening: false,
      },
      requisitionList: [],
    };

    setIsFromSearchAndSort(false);
    setSearchAndSortTypes(clearedFilters);
    setTempSearchAndSort(clearedFilters);
  };

  // istanbul ignore next
  const onGetFilterCount = () => {
    let count = 0;

    const {
      isInterventionEnabled,
      isFavouriteApplied,
      isPriorityCandidates,
      kioskEnabled,
      candidateEngagement,
      backgroundCheck,
      hireStatus,
      workbrightStatus,
      languageEvaluation,
      availabilityFit,
      requisitionList,
      modernHireStatus,
      rating,
      availability,
    } = tempSearchAndSort;

    // Boolean filters
    if (isInterventionEnabled) count += 1;
    if (isFavouriteApplied) count += 1;
    if (isPriorityCandidates) count += 1;
    if (kioskEnabled) count += 1;

    // Candidate Engagement
    Object.values(candidateEngagement).forEach((value) => {
      if (value !== '') count += 1;
    });

    // Array filters
    count += backgroundCheck.length;
    count += hireStatus.length;
    count += workbrightStatus.length;
    count += languageEvaluation.length;
    count += availabilityFit.length;
    count += requisitionList.length;
    count += modernHireStatus.length;

    // Ratings
    [1, 2, 3, 4, 5].forEach((r) => {
      if (rating.includes(r)) count += 1;
    });

    // Availability
    if (availability.day) count += 1;
    if (availability.evening) count += 1;
    if (availability.night) count += 1;

    setFilterCount(count);
  };

  useEffect(() => {
    onGetFilterCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSearchAndSort]);

  const onApplyFilters = () => {
    setSearchAndSortTypes({ ...tempSearchAndSort });
    setIsFromSearchAndSort(false);
  };

  // function to check if there are any active filters
  const hasActiveFilters = (): boolean => {
    const keysToCheck: (keyof SearchAndSortTypes)[] = [
      'isFavouriteApplied',
      'isPriorityCandidates',
      'kioskEnabled',
      'isInterventionEnabled',
      'backgroundCheck',
      'workbrightStatus',
      'hireStatus',
      'languageEvaluation',
      'availabilityFit',
      'modernHireStatus',
      'requisitionList',
    ];

    const engagementKeys: (keyof EngagementTypes)[] = [
      'apointmentRequested',
      'Hired',
      'offerMade',
      'offerRejected',
      'appoinmentAccepted',
      'appoinmentDeclined',
      'appoinmentCancled',
      'noAppointmentRequest',
      'offerAccepted',
      'interestRevoked',
      'contactRequestDeclined',
      'onDemandInterviewRequested',
      'onDemandInterviewAccepted',
      'onDemandInterviewCanceled',
    ];

    const hasEngagementFilters = engagementKeys.some((key) => tempSearchAndSort.candidateEngagement[key]);
    const hasRating = tempSearchAndSort.rating?.some((r) => r !== 0);
    // const hasAvailability = Object.values(tempSearchAndSort.availability || {}).some(Boolean);

    return (
      keysToCheck.some((key) => {
        const value = tempSearchAndSort[key];
        return Array.isArray(value) ? value.length > 0 : Boolean(value);
      }) ||
      hasEngagementFilters ||
      hasRating
    );
  };

  // istanbul ignore next
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const currentlyActive = hasActiveFilters();
    if (prevHasActiveFilters.current && !currentlyActive) {
      onApplyFilters();
    }

    prevHasActiveFilters.current = currentlyActive;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSearchAndSort]);

  return (
    <Box css={searchAndSortWrapper}>
      <Box className="searchSidebar" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box px={3} py={4} className="searchAndSortHeader">
          <Flex>
            <Box onClick={onCloseSearch} style={{ cursor: 'pointer', marginTop: 2 }}>
              <AiOutlineArrowLeft />
            </Box>
            <Text
              px={3}
              style={{
                fontSize: '16px',
                fontWeight: 'bolder',
                paddingBottom: 14,
              }}
            >
              {t('searchAndSort.advanceSearch')}
            </Text>
          </Flex>
          <Divider />
        </Box>
        {filterCount > 0 && (
          <Box
            mx={2}
            px={2}
            py={2}
            backgroundColor="rgba(33, 150, 243, 0.1)"
            mb={4}
            borderRadius={10}
            height="auto"
            maxHeight="130px"
            overflowY="auto"
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Flex>
                <Text fontSize="14px" fontWeight="bold" mr={4}>
                  {t('searchAndSort.activeFilters')}
                </Text>
                <Box backgroundColor="rgba(33, 150, 243, 0.3)" px={2} py={0.5} borderRadius={6}>
                  <Text fontSize="14px" fontWeight="bold" color={colors.blue.default}>
                    {filterCount}
                  </Text>
                </Box>
              </Flex>

              <Box fontSize="14px" fontWeight={700} color={colors.blue[500]} mr={2} onClick={clearAllFilters} cursor="pointer">
                {t('searchAndSort.resetBtn')}
              </Box>
            </Flex>

            {/* <Box px="8px" py="8px" paddingTop={0}>
              <HStack flexWrap="wrap">
                {appliedFilters.map((filterItem) => (
                  <Button
                    key={filterItem}
                    size="xs"
                    fontWeight="normal"
                    mb="12px"
                    ml={2}
                    rightIcon={<RiCloseFill onClick={() => {}} />}
                    colorScheme="blue"
                    variant="solid"
                  >
                    {t(`searchAndSort.filterStatus.${filterItem}`)}
                  </Button>
                ))}
              </HStack>
            </Box> */}
          </Box>
        )}
        <Box className="searchAndSortBody" style={{ flex: 1, overflowY: 'auto' }}>
          <Box>
            <Box mb={2}>
              <Box px={4} pb="1">
                <FavoriteCandidateView
                  tempSearchAndSortType={tempSearchAndSort}
                  setTempSearchAndSortTypes={setTempSearchAndSort}
                />
              </Box>
              {isPriorityFeatureEnabled && (
                <Box px={4} pb="1">
                  <PriorityCandidatesView
                    tempSearchAndSortType={tempSearchAndSort}
                    setTempSearchAndSortTypes={setTempSearchAndSort}
                  />
                </Box>
              )}
              <Box px={4} pb="1">
                <KioskCandidatesView tempSearchAndSortType={tempSearchAndSort} setTempSearchAndSortTypes={setTempSearchAndSort} />
              </Box>
              <Box px={4} pb="1">
                <ActionRequiredCandidateView
                  tempSearchAndSortType={tempSearchAndSort}
                  setTempSearchAndSortTypes={setTempSearchAndSort}
                />
              </Box>
            </Box>
            <Box>
              <Accordion allowMultiple defaultIndex={[0]}>
                <AccordionItem style={{ border: 'none' }}>
                  <h2>
                    <AccordionButton style={{ paddingLeft: 0 }}>
                      <Box flex="1" textAlign="left" px={4}>
                        <Text
                          style={{
                            fontSize: '14px',
                            fontWeight: 'bolder',
                          }}
                        >
                          {t('searchAndSort.candidateEngagement')}
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel>
                    <CandidateEngagementView
                      tempSearchAndSortType={tempSearchAndSort}
                      setTempSearchAndSortTypes={setTempSearchAndSort}
                    />
                  </AccordionPanel>
                </AccordionItem>
                <AccordionItem style={{ border: 'none' }}>
                  <h2>
                    <AccordionButton style={{ paddingLeft: 0 }}>
                      <Box flex="1" textAlign="left" px={4}>
                        <Text
                          style={{
                            fontSize: '14px',
                            fontWeight: 'bolder',
                          }}
                        >
                          {t('searchAndSort.ratings')}
                        </Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel>
                    <RatingsView tempSearchAndSortType={tempSearchAndSort} setTempSearchAndSortTypes={setTempSearchAndSort} />
                  </AccordionPanel>
                </AccordionItem>

                {isHireFormEnabled && hireFormEnabledPositions && hireFormEnabledPositions.length > 0 && (
                  <AccordionItem style={{ border: 'none' }}>
                    <h2>
                      <AccordionButton style={{ paddingLeft: 0 }}>
                        <Box flex="1" textAlign="left" px={4}>
                          <Text
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bolder',
                            }}
                          >
                            {t('searchAndSort.hireStatus')}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel>
                      <HireStatus tempSearchAndSortType={tempSearchAndSort} setTempSearchAndSortTypes={setTempSearchAndSort} />
                    </AccordionPanel>
                  </AccordionItem>
                )}

                {checkPermission({ permissions: [EmployeePermissionsEnum.I9_ADMIN] }) && (
                  <AccordionItem style={{ border: 'none' }}>
                    <h2>
                      <AccordionButton style={{ paddingLeft: 0 }}>
                        <Box flex="1" textAlign="left" px={4}>
                          <Text
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bolder',
                            }}
                          >
                            {t('searchAndSort.i9VerificationStatus')}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel>
                      <WorkBrightStatus
                        tempSearchAndSortType={tempSearchAndSort}
                        setTempSearchAndSortTypes={setTempSearchAndSort}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                )}

                {/* {isModernHireFormEnabled && ( */}
                {/*  <AccordionItem style={{ border: 'none' }}> */}
                {/*    <h2> */}
                {/*      <AccordionButton style={{ paddingLeft: 0 }}> */}
                {/*        <Box flex="1" textAlign="left" px={4}> */}
                {/*          <Text */}
                {/*            style={{ */}
                {/*              fontSize: '14px', */}
                {/*              fontWeight: 'bolder', */}
                {/*            }} */}
                {/*          > */}
                {/*            {t('searchAndSort.modernHireStatus')} */}
                {/*          </Text> */}
                {/*        </Box> */}
                {/*        <AccordionIcon /> */}
                {/*      </AccordionButton> */}
                {/*    </h2> */}
                {/*    <AccordionPanel> */}
                {/*      <ModernHireStatus /> */}
                {/*    </AccordionPanel> */}
                {/*  </AccordionItem> */}
                {/* )} */}

                {isCertnFeatureEnabled && isCertnEnabled && (
                  <AccordionItem style={{ border: 'none' }}>
                    <h2>
                      <AccordionButton style={{ paddingLeft: 0 }}>
                        <Box flex="1" textAlign="left" px={4}>
                          <Text
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bolder',
                            }}
                          >
                            {t('searchAndSort.backgroundCheck')}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel>
                      <BackgroundCheck
                        tempSearchAndSortType={tempSearchAndSort}
                        setTempSearchAndSortTypes={setTempSearchAndSort}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                )}
                {/* {isAvailabilitySearchEnabledInGlobalSearch && ( */}
                {/*  <AccordionItem style={{ border: 'none' }}> */}
                {/*    <h2> */}
                {/*      <AccordionButton style={{ paddingLeft: 0 }}> */}
                {/*        <Box flex="1" textAlign="left" px={4}> */}
                {/*          <Text */}
                {/*            style={{ */}
                {/*              fontSize: '14px', */}
                {/*              fontWeight: 'bolder', */}
                {/*            }} */}
                {/*          > */}
                {/*            {t('searchAndSort.availability')} */}
                {/*          </Text> */}
                {/*        </Box> */}
                {/*        <AccordionIcon /> */}
                {/*      </AccordionButton> */}
                {/*    </h2> */}
                {/*    <AccordionPanel> */}
                {/*      <Availability /> */}
                {/*    </AccordionPanel> */}
                {/*  </AccordionItem> */}
                {/* )} */}

                {isLanguageEvaluationEnabled && (
                  <AccordionItem style={{ border: 'none' }}>
                    <h2>
                      <AccordionButton style={{ paddingLeft: 0 }}>
                        <Box flex="1" textAlign="left" px={4}>
                          <Text
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bolder',
                            }}
                          >
                            {t('audioEvaluation:languageEvaluation')}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel>
                      <LanguageEvaluation
                        tempSearchAndSortType={tempSearchAndSort}
                        setTempSearchAndSortTypes={setTempSearchAndSort}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                )}

                {isScheduleMatchEnabled && (
                  <AccordionItem style={{ border: 'none' }}>
                    <h2>
                      <AccordionButton style={{ paddingLeft: 0 }}>
                        <Box flex="1" textAlign="left" px={4}>
                          <Text
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bolder',
                            }}
                          >
                            {t('searchAndSort.availabilityFit')}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel>
                      <AvailabilityFit
                        tempSearchAndSortType={tempSearchAndSort}
                        setTempSearchAndSortTypes={setTempSearchAndSort}
                      />
                    </AccordionPanel>
                  </AccordionItem>
                )}

                {isRequsitionEnabled && isPositionCandidatesPage() && (
                  /* istanbul ignore next */
                  <AccordionItem style={{ border: 'none' }}>
                    <h2>
                      <AccordionButton style={{ paddingLeft: 0 }}>
                        <Box flex="1" textAlign="left" px={4}>
                          <Text
                            style={{
                              fontSize: '14px',
                              fontWeight: 'bolder',
                            }}
                          >
                            Requisitions List
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel>
                      <RequisitionsList />
                    </AccordionPanel>
                  </AccordionItem>
                )}
              </Accordion>
            </Box>
          </Box>
        </Box>

        <Box display="flex" justifyContent="space-between" px={3} py={3} style={{ borderTop: '1px solid #e2e8f0' }}>
          <Button
            data-testid="clearFilterBtn"
            variant="ghost"
            size="sm"
            colorScheme="blue"
            disabled={!hasActiveFilters()}
            onClick={clearAllFilters}
          >
            {t('searchAndSort.clearButton')}
          </Button>

          <Button
            data-testid="applyBtn"
            size="sm"
            colorScheme="blue"
            onClick={onApplyFilters}
            disabled={!hasActiveFilters() || !isFromSearchAndSort}
          >
            {t('searchAndSort.applyButton')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
